import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { GetMakeModel } from "../../Service/_index";
import { ToastError } from "../../UiComponents/Toaster/Toast";

/**
 * Add / edit one per-user Make & Model discount row.
 *
 * Unlike AddBankDrawer this does NOT persist on its own — it hands the row
 * back to AddUser, which submits the whole array with the user. AddBankDrawer
 * saves immediately to /updateBankDetailsByUserId/:id, which needs an existing
 * user _id and therefore silently fails while CREATING a user.
 *
 * On EDIT the Make & Model is read-only: only the discount may change.
 */
const AddMakeModelDrawer = ({
  formType,
  selectedRow,
  existingRows = [],
  onSave,
  onClose,
}) => {
  const isEdit = formType === "edit";

  const [makeModelOptions, setMakeModelOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMakeModel, setSelectedMakeModel] = useState(null);
  const [discount, setDiscount] = useState(
    selectedRow?.values?.Discount !== undefined &&
      selectedRow?.values?.Discount !== null
      ? String(selectedRow.values.Discount)
      : ""
  );
  // Opt-in switch: only when ON does the Online Policy use this discount
  // instead of the shared Make & Model master value.
  const [useOwnDiscount, setUseOwnDiscount] = useState(
    selectedRow?.values?.useOwnDiscount === true
  );
  const [error, setError] = useState("");

  // Load the Make & Model master for the picker
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await GetMakeModel();
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const options = rows
          .filter((item) => item.isEnabled)
          .map((item) => ({
            _id: item._id,
            make: item.make,
            model: item.model,
            vehicleVariant: item.vehicleVariant || "",
            masterDiscount: item.discount,
            label: `${item.make} - ${item.model}${item.vehicleVariant ? ` (${item.vehicleVariant})` : ""
              }`,
          }));
        setMakeModelOptions(options);


        // On edit, resolve the stored id back to its option so the (read-only)
        // name can be shown.
        if (isEdit && selectedRow?.values?.makemodel) {
          const storedId = String(selectedRow.values.makemodel);
          setSelectedMakeModel(
            options.find((o) => String(o._id) === storedId) || null
          );
        }
      } catch (e) {
        if (!cancelled) ToastError("Failed to load Make & Model list");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, selectedRow]);

  // Make & Models already on this user are left OUT of the dropdown — offering
  // one only to reject it on save ("already in the list") wastes the operator's
  // time. On edit the field is read-only, so the full list is fine there.
  const selectableOptions = React.useMemo(() => {
    if (isEdit) return makeModelOptions;
    const taken = new Set(
      (existingRows || []).map((row) => String(row.makemodel))
    );
    return makeModelOptions.filter((option) => !taken.has(String(option._id)));
  }, [makeModelOptions, existingRows, isEdit]);

  const handleSave = () => {
    setError("");

    if (!isEdit && !selectedMakeModel) {
      setError("Please select a Make & Model");
      return;
    }

    // The discount only matters when the switch is on — otherwise the master
    // value is used and this field is irrelevant.
    const discountValue = Number(discount);
    if (useOwnDiscount) {
      if (discount === "" || Number.isNaN(discountValue)) {
        setError("Please enter a discount, or turn off \"Use separate discount\"");
        return;
      }
      if (discountValue < 0 || discountValue > 100) {
        setError("Discount must be between 0 and 100");
        return;
      }
    }

    // Adding the same make/model twice would make the discount ambiguous.
    if (!isEdit) {
      const alreadyAdded = existingRows.some(
        (row) => String(row.makemodel) === String(selectedMakeModel._id)
      );
      if (alreadyAdded) {
        setError("This Make & Model is already in the list");
        return;
      }
    }

    onSave(
      {
        makemodel: isEdit ? selectedRow.values.makemodel : selectedMakeModel._id,
        Discount: useOwnDiscount ? discountValue : null,
        useOwnDiscount,
        // Kept for display only — the reference above is the source of truth.
        make: isEdit ? selectedRow.values.make : selectedMakeModel.make,
        model: isEdit ? selectedRow.values.model : selectedMakeModel.model,
      },
      isEdit ? selectedRow.index : undefined
    );
  };

  return (
    <Box sx={{ pt: 3, px: 3, pb: 0, minWidth: 480 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
          {isEdit ? "Edit Discount" : "Add Make & Model Discount"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography sx={{ mb: 1, fontWeight: 500, fontSize: 13 }}>
            Make &amp; Model *
          </Typography>

          {isEdit ? (
            // Read-only on edit — only the discount may be changed.
            <>
              <input
                className="textField w-100"
                readOnly
                value={
                  selectedMakeModel?.label ||
                  `${selectedRow?.values?.make || ""} - ${selectedRow?.values?.model || ""}`
                }
                style={{ backgroundColor: "#f5f6f7", color: "#667085" }}
              />
              <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.5 }}>
                Make &amp; Model cannot be changed — only the discount.
              </Typography>
            </>
          ) : (
            <Autocomplete
              // index.scss sets .MuiOutlinedInput-notchedOutline to
              // transparent globally, so MUI's own border never shows. The app
              // draws input borders through these classes instead.
              className="AutoComplete_InputBox"
              // NOT disablePortal: inside a Dialog the inline popup is clipped
              // by the paper and scrolls the dialog instead of overlaying it.
              options={selectableOptions}
              loading={loading}
              value={selectedMakeModel}
              noOptionsText={
                makeModelOptions.length > 0
                  ? "Every Make & Model is already added"
                  : "No Make & Model available"
              }
              ListboxProps={{ style: { maxHeight: 260 } }}
              componentsProps={{
                popper: { style: { zIndex: 1500 } },
              }}
              onChange={(e, v) => setSelectedMakeModel(v)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                String(option?._id) === String(value?._id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Make & Model"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={useOwnDiscount}
                onChange={(e) => {
                  const on = e.target.checked;
                  setUseOwnDiscount(on);
                  setError("");
                  // Clear the value when switching off, so a stale discount
                  // cannot look active.
                  if (!on) setDiscount("");
                }}
              />
            }
            label="Use separate discount for this Make & Model"
          />
          <Typography sx={{ fontSize: 11, color: "#667085", mt: -0.5 }}>
            {useOwnDiscount
              ? "Online Policies will use the discount below."
              : "Online Policies will use the Make & Model master discount."}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ mb: 1, fontWeight: 500, fontSize: 13 }}>
            Discount (%){useOwnDiscount ? " *" : ""}
          </Typography>
          <input
            className="textField w-100"
            value={discount}
            placeholder={useOwnDiscount ? "e.g. 85" : "Turn on the switch above to set this"}
            disabled={!useOwnDiscount}
            style={!useOwnDiscount ? { backgroundColor: "#f5f6f7", color: "#98a2b3" } : undefined}
            onChange={(e) => {
              const cleaned = e.target.value
                .replace(/[^0-9.]/g, "")
                .replace(/(\..*)\./g, "$1");
              setDiscount(cleaned);
            }}
          />
          {selectedMakeModel?.masterDiscount !== undefined &&
            selectedMakeModel?.masterDiscount !== null &&
            selectedMakeModel?.masterDiscount !== "" ? (
            <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.5 }}>
              Master discount for this record: {selectedMakeModel.masterDiscount}%
            </Typography>
          ) : null}
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Typography>
          </Grid>
        )}

      </Grid>

      {/* Project button theme — same classes every other dialog uses
          (UiComponents/Scss/Buttons.scss: brand navy #1E3A5F). Plain MUI
          contained/outlined buttons did not match. */}
      <div className="Dialog_Footer" style={{ marginTop: 20 }}>
        <Button onClick={onClose} className="Dialog_Cancel">
          Cancel
        </Button>
        <Button onClick={handleSave} className="Dialog_Save_Exit">
          {isEdit ? "Update" : "Add"}
        </Button>
      </div>
    </Box>
  );
};

export default AddMakeModelDrawer;
